import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { QRCodeService } from '../../../services/qrCodeService';
import { BookingService } from '../../../services/bookingService';
import SmsService from '../../../services/smsService';

const TicketModal = ({ booking, isOpen, onClose }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsPhoneNumber, setSmsPhoneNumber] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [smsError, setSmsError] = useState('');
  const [smsSuccess, setSmsSuccess] = useState('');

  useEffect(() => {
    if (booking && isOpen) {
      generateQRCode();
    }
  }, [booking, isOpen]);

  // Reset SMS states when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShowSmsModal(false);
      setSmsPhoneNumber('');
      setSmsError('');
      setSmsSuccess('');
      setSendingSms(false);
    }
  }, [isOpen]);

  const generateQRCode = async () => {
    if (!booking) return;
    
    setLoading(true);
    try {
      const qrUrl = QRCodeService?.createBookingQRCode(booking, '300x300');
      setQrCodeUrl(qrUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatBooking = () => {
    if (!booking) return null;
    return BookingService?.formatBooking(booking);
  };

  const formattedBooking = formatBooking();

  if (!isOpen || !formattedBooking) return null;

  const eventDate = formattedBooking?.eventDate ? new Date(formattedBooking.eventDate) : null;

  const handleDownloadTicket = () => {
    // Create a printable ticket
    const printWindow = window.open('', '_blank');
    printWindow?.document?.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Event Ticket - ${formattedBooking?.eventTitle}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .ticket { border: 2px solid #000; padding: 20px; max-width: 400px; }
            .header { text-align: center; border-bottom: 1px solid #ccc; padding-bottom: 10px; margin-bottom: 20px; }
            .qr-code { text-align: center; margin: 20px 0; }
            .details { margin: 10px 0; }
            .label { font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="ticket">
            <div class="header">
              <h2>${formattedBooking?.eventTitle}</h2>
              <p>Booking Reference: ${formattedBooking?.reference}</p>
            </div>
            <div class="qr-code">
              <img src="${qrCodeUrl}" alt="QR Code" style="width: 200px; height: 200px;" />
            </div>
            <div class="details">
              <p><span class="label">Date:</span> ${eventDate ? eventDate?.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              }) : 'TBD'}</p>
              <p><span class="label">Time:</span> ${eventDate ? eventDate?.toLocaleTimeString('en-US', { 
                hour: 'numeric', 
                minute: '2-digit' 
              }) : 'TBD'}</p>
              <p><span class="label">Venue:</span> ${formattedBooking?.eventVenue || 'TBD'}</p>
              <p><span class="label">Ticket Type:</span> ${formattedBooking?.ticketName}</p>
              <p><span class="label">Quantity:</span> ${formattedBooking?.quantity}</p>
              <p><span class="label">Attendee:</span> ${formattedBooking?.attendeeName}</p>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow?.document?.close();
    printWindow?.print();
  };

  const handleShareTicket = async () => {
    const shareData = {
      title: `My ticket for ${formattedBooking?.eventTitle}`,
      text: `I'm attending ${formattedBooking?.eventTitle} on ${eventDate ? eventDate?.toLocaleDateString() : 'TBD'}`,
      url: QRCodeService?.createShareableURL(booking)
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback to copying to clipboard
        await navigator.clipboard?.writeText(shareData?.url);
        alert('Ticket link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing ticket:', error);
    }
  };

  const handleSmsShare = () => {
    setShowSmsModal(true);
    setSmsError('');
    setSmsSuccess('');
  };

  const handleSendSms = async () => {
    if (!smsPhoneNumber?.trim()) {
      setSmsError('Please enter a phone number');
      return;
    }

    if (!SmsService?.validatePhoneNumber(smsPhoneNumber)) {
      setSmsError('Please enter a valid phone number (e.g., +1234567890 or 1234567890)');
      return;
    }

    setSendingSms(true);
    setSmsError('');
    setSmsSuccess('');

    try {
      const formattedPhone = SmsService?.formatPhoneNumber(smsPhoneNumber);
      const result = await SmsService?.sendTicketDetails(booking, formattedPhone);

      if (result?.success) {
        setSmsSuccess(`Ticket details sent successfully to ${formattedPhone}!`);
        setSmsPhoneNumber('');
        setTimeout(() => {
          setShowSmsModal(false);
          setSmsSuccess('');
        }, 2000);
      } else {
        setSmsError(result?.error || 'Failed to send SMS. Please try again.');
      }
    } catch (error) {
      setSmsError('Unable to send SMS. Please check your connection and try again.');
    } finally {
      setSendingSms(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Your Ticket</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Event Info */}
          <div className="text-center space-y-2">
            <h3 className="text-2xl font-bold text-foreground">{formattedBooking?.eventTitle}</h3>
            <p className="text-muted-foreground">Booking Reference: {formattedBooking?.reference}</p>
          </div>

          {/* QR Code */}
          <div className="text-center">
            <div className="inline-block p-4 bg-white rounded-lg border-2 border-dashed border-muted-foreground">
              {loading ? (
                <div className="w-[300px] h-[300px] flex items-center justify-center">
                  <Icon name="Loader2" size={32} className="animate-spin text-primary" />
                </div>
              ) : qrCodeUrl ? (
                <img 
                  src={qrCodeUrl} 
                  alt="Event Ticket QR Code" 
                  className="w-[300px] h-[300px]"
                />
              ) : (
                <div className="w-[300px] h-[300px] flex items-center justify-center text-muted-foreground">
                  <Icon name="QrCode" size={64} />
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Show this QR code at the event entrance
            </p>
          </div>

          {/* Event Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Icon name="Calendar" size={16} className="text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium text-foreground">
                    {eventDate ? eventDate?.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    }) : 'TBD'}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Icon name="Clock" size={16} className="text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Time</p>
                  <p className="font-medium text-foreground">
                    {eventDate ? eventDate?.toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit' 
                    }) : 'TBD'}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Icon name="MapPin" size={16} className="text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Venue</p>
                  <p className="font-medium text-foreground">
                    {formattedBooking?.eventVenue || 'TBD'}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Icon name="Ticket" size={16} className="text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Ticket Type</p>
                  <p className="font-medium text-foreground">{formattedBooking?.ticketName}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Icon name="Users" size={16} className="text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Quantity</p>
                  <p className="font-medium text-foreground">{formattedBooking?.quantity}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Icon name="User" size={16} className="text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Attendee</p>
                  <p className="font-medium text-foreground">{formattedBooking?.attendeeName}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center justify-center">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              formattedBooking?.bookingStatus === 'confirmed' ?'bg-green-100 text-green-800 border border-green-200' :'bg-yellow-100 text-yellow-800 border border-yellow-200'
            }`}>
              <Icon name="CheckCircle" size={14} className="mr-2" />
              {formattedBooking?.bookingStatus?.charAt(0)?.toUpperCase() + formattedBooking?.bookingStatus?.slice(1)}
            </span>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
            <Button 
              variant="default" 
              className="flex-1" 
              onClick={handleDownloadTicket}
              disabled={!qrCodeUrl}
            >
              <Icon name="Download" size={16} className="mr-2" />
              Download Ticket
            </Button>
            
            <Button 
              variant="outline" 
              className="flex-1" 
              onClick={handleShareTicket}
            >
              <Icon name="Share" size={16} className="mr-2" />
              Share
            </Button>

            <Button 
              variant="outline" 
              className="flex-1" 
              onClick={handleSmsShare}
            >
              <Icon name="MessageSquare" size={16} className="mr-2" />
              Send SMS
            </Button>
          </div>
        </div>
      </div>
      {/* SMS Modal */}
      {showSmsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full">
            {/* SMS Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Send Ticket via SMS</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowSmsModal(false)}>
                <Icon name="X" size={16} />
              </Button>
            </div>

            {/* SMS Content */}
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-4">
                  Enter a phone number to send ticket details for "{formattedBooking?.eventTitle}"
                </p>
                
                <Input
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={smsPhoneNumber}
                  onChange={(e) => {
                    setSmsPhoneNumber(e?.target?.value);
                    setSmsError('');
                  }}
                  disabled={sendingSms}
                  className="w-full"
                />
                
                <p className="text-xs text-muted-foreground mt-2">
                  Supported formats: +1234567890, 1234567890, (123) 456-7890
                </p>
              </div>

              {/* Error Message */}
              {smsError && (
                <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <Icon name="AlertCircle" size={16} className="text-red-600" />
                  <p className="text-sm text-red-600">{smsError}</p>
                </div>
              )}

              {/* Success Message */}
              {smsSuccess && (
                <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Icon name="CheckCircle" size={16} className="text-green-600" />
                  <p className="text-sm text-green-600">{smsSuccess}</p>
                </div>
              )}

              {/* SMS Actions */}
              <div className="flex space-x-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowSmsModal(false)}
                  disabled={sendingSms}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  className="flex-1"
                  onClick={handleSendSms}
                  disabled={sendingSms || !smsPhoneNumber?.trim()}
                >
                  {sendingSms ? (
                    <>
                      <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Icon name="Send" size={16} className="mr-2" />
                      Send SMS
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketModal;